/**
 * TypeScript models for the shared lead shape that the browser submit path
 * projects into LeadIntake XML.
 *
 * The shape remains aligned with the LMD lead/upsert vocabulary because the same
 * fields were proven by the MLA/EPS server pattern, but this browser MVP is
 * LeadIntake-only: no Empower loan is created, no secondary LMD Upsert call is
 * issued, and the lead carries no `loanId`/`loanNumber` or loan GUID.
 */

/** Legacy LMD Upsert endpoint path retained for tests that assert it is not called. */
export const LMD_UPSERT_LEAD_PATH = "/api/v1/Lead/UpsertLead/";

/** Wave 3 lead constants. These are fixed by the WI contract, not user input. */
export const LEAD_SOURCE_TURBOVETS = "TurboVets_VA";
export const LEAD_OWNER_POSBANKER = "posbanker";
export const LEAD_SUPPLEMENTAL_STATUS_UNCALLABLE = "U";
export const LMD_SOURCE_SYSTEM = "DigitalApp";
export const LMD_ROUTING_INTENT = "NewLead";

export type LmdLoanPurpose = "Purchase" | "RefinanceCashOut" | "RefinanceRateTerm";

export type LmdPropertyType =
  | "SingleFamily"
  | "MultiFamily"
  | "Condo"
  | "Townhouse"
  | "Coop"
  | "Mobile"
  | "Manufactured";

export type LmdCreditProfile = "Poor" | "Fair" | "Good" | "Excellent";

/**
 * LMD `MilitaryBranch` enum values, serialized as their exact names by the LMD
 * API's `StringEnumConverter`. Pinned to LD.LeadEngineering.API commit
 * 195e6cab5c68b2da25cf2113d05dade18e592487
 * (LD.Leads.LeadManagement.Common/MilitaryBranch.cs, blob
 * 03167d4bb3e5664c55b715c9e4b0062513eab9b0).
 */
export type LmdMilitaryBranch =
  | "Army"
  | "Navy"
  | "AirForce"
  | "MarineCorps"
  | "SpaceForce"
  | "CoastGuard"
  | "ArmyNationalGuard"
  | "AirNationalGuard";

/**
 * An LMD lead contact. The lead carries the applicant as `borrower` and, when
 * the application has one, the optional secondary contact as `coBorrower`; both
 * are the same existing LMD contact shape, distinguished on the wire only by
 * `isPrimaryBorrower`.
 *
 * `phone1Number` accepts the exact ten-digit string representation this SPA
 * submits for both applicant and co-borrower contacts, preserving leading
 * zeroes that would be lost by numeric coercion.
 *
 * The mailing fields are the contact's own optional mailing address. They are
 * emitted only when supplied, so an omitted address is absent rather than blank.
 */
export interface LmdBorrower {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  phone1Number?: string | number;
  mailStreetAddress?: string;
  mailCity?: string;
  mailState?: string;
  mailZipCode?: string;
  /** True for the applicant, false for the optional co-borrower. */
  isPrimaryBorrower?: boolean;
}

export interface LmdApplication {
  loanPurpose?: LmdLoanPurpose;
  propertyType?: LmdPropertyType;
  creditProfile?: LmdCreditProfile;
  isMilitary?: boolean;
  militaryBranch?: LmdMilitaryBranch;
  propertyStreetAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZipCode?: string;
  loanAmount?: number;
  estimatedPropertyValue?: number;
  downPayment?: number;
  currentLoanBalance?: number;
}

export interface LmdSupplemental {
  status: string;
}

export interface LmdLead {
  leadGuid?: string;
  leadSource: string;
  originalLeadSource?: string;
  leadOwner: string;
  isCallable: boolean;
  supplemental: LmdSupplemental;
  borrower: LmdBorrower;
  /**
   * The optional secondary contact. The property is present only for an
   * application that opted one in and still holds them: a declined, skipped, or
   * removed co-borrower is omitted entirely rather than sent as null or blank.
   */
  coBorrower?: LmdBorrower;
  application: LmdApplication;
}

/** Shared lead envelope that is projected into LeadIntake XML. */
export interface LmdUpsertLeadRequest {
  lead: LmdLead;
  sourceSystem: string;
  routingIntent: string;
}

/**
 * Normalized, sanitized outcome of a successful LeadIntake submission. Lead-only:
 * there is no Empower loan id/number, status link, or detail link. A returned
 * LeadIntake `LDGUID` is captured when present.
 */
export interface LmdSubmissionResult {
  status: string;
  submittedAt: string;
  leadGuid?: string;
}

/**
 * Maps the SPA loan purpose (and refinance type) to the LMD loan-purpose enum.
 * Mirrors EPS `TVReferralMapper.MapLoanPurposeToLmd`.
 */
export function mapLoanPurposeToLmd(
  purpose: "purchase" | "refinance",
  refinanceType?: string,
): LmdLoanPurpose {
  if (purpose === "refinance") {
    return refinanceType === "CashOutRefinance" ? "RefinanceCashOut" : "RefinanceRateTerm";
  }
  return "Purchase";
}

/**
 * Maps the SPA property-type value to the LMD property-type enum. Unknown values
 * fall back to a single-family detached residence, matching EPS
 * `TVReferralMapper.MapPropertyTypeToLmd`.
 */
export function mapPropertyTypeToLmd(propertyType: string): LmdPropertyType {
  switch (propertyType) {
    case "SingleFamily":
    case "MultiFamily":
    case "Condo":
    case "Townhouse":
    case "Coop":
    case "Mobile":
    case "Manufactured":
      return propertyType;
    default:
      return "SingleFamily";
  }
}

/**
 * Maps a TurboVets military component choice to the LMD `MilitaryBranch` enum,
 * or `undefined` when there is no exact LMD value for it.
 */
export function mapMilitaryBranchToLmd(component?: string): LmdMilitaryBranch | undefined {
  switch (component) {
    case "Army":
      return "Army";
    case "Navy":
      return "Navy";
    case "Air Force":
      return "AirForce";
    case "Marine Corps":
      return "MarineCorps";
    case "Coast Guard":
      return "CoastGuard";
    case "Space Force":
      return "SpaceForce";
    case "National Guard / Reserves":
      return "ArmyNationalGuard";
    default:
      return undefined;
  }
}

/**
 * Maps the SPA credit-score range to the LMD credit-profile enum, or `undefined`
 * when absent/unrecognized. Mirrors EPS `TVReferralMapper.MapCreditScoreRangeToLmd`.
 */
export function mapCreditProfileToLmd(creditScoreRange?: string): LmdCreditProfile | undefined {
  switch (creditScoreRange) {
    case "below 620":
      return "Poor";
    case "620-679":
      return "Fair";
    case "680-739":
      return "Good";
    case "740+":
      return "Excellent";
    default:
      return undefined;
  }
}
